import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface Machine {
  Id?: number;
  Name: string;
  Description?: string;
  MachineNumber?: string;
  MaintenanceRequired: boolean;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface MachineListResponse {
  data: Machine[];
  total: number;
}

export interface MachineSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  maintenanceRequired?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// API Service
export const machineApi = {
  // CRUD Operations
  getAll: (params?: MachineSearchParams): Promise<MachineListResponse> =>
    api.get('/machine', params),

  getById: (id: number): Promise<Machine> =>
    api.get(`/machine/${id}`),

  create: (machine: Omit<Machine, 'Id'>): Promise<Machine> =>
    api.post('/machine', machine),

  update: (id: number, machine: Partial<Machine>): Promise<Machine> =>
    api.put(`/machine/${id}`, machine),

  delete: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/machine/${id}`),

  // Search and Filter
  search: (params: MachineSearchParams): Promise<MachineListResponse> =>
    api.post('/machine/search', params),

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/machine/bulk-activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/machine/bulk-deactivate', { ids }),

  bulkDelete: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.post('/machine/bulk-delete', { ids }),

  // Statistics
  getStatistics: (): Promise<{
    total: number;
    active: number;
    inactive: number;
    maintenanceRequired: number;
  }> =>
    api.get('/machine/statistics'),

  // Export
  exportToCSV: (params?: MachineSearchParams): Promise<Blob> =>
    api.getBlob('/machine/export', { params }),

  // Validation
  validation: {
    validateMachine: (machine: Partial<Machine>): string[] => {
      const errors: string[] = [];

      // Name validation
      if (!machine.Name || machine.Name.trim().length === 0) {
        errors.push('Machine Name is required');
      } else if (machine.Name.trim().length < 2) {
        errors.push('Machine Name must be at least 2 characters long');
      } else if (machine.Name.trim().length > 100) {
        errors.push('Machine Name must be less than 100 characters');
      }

      // Description validation (optional but if provided, validate length)
      if (machine.Description && machine.Description.trim().length > 500) {
        errors.push('Description must be less than 500 characters');
      }

      // Machine Number validation (optional but if provided, validate length)
      if (machine.MachineNumber && machine.MachineNumber.trim().length > 50) {
        errors.push('Machine Number must be less than 50 characters');
      }

      // Maintenance Required validation
      if (machine.MaintenanceRequired === undefined || machine.MaintenanceRequired === null) {
        errors.push('Maintenance Required status is required');
      }

      // Active validation
      if (machine.Active === undefined || machine.Active === null) {
        errors.push('Active status is required');
      }

      return errors;
    },

    validateBulkOperation: (ids: number[]): string[] => {
      const errors: string[] = [];

      if (!ids || ids.length === 0) {
        errors.push('At least one Machine must be selected');
      }

      if (ids.length > 100) {
        errors.push('Cannot perform bulk operation on more than 100 items at once');
      }

      return errors;
    }
  }
};

export default machineApi; 